import React from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle, ChevronDown } from 'lucide-react'
import HeroMockup from './HeroMockup'

const RISK_BADGES = ['Sem cartão de crédito', '7 dias grátis', 'Cancele quando quiser']

const PROOF_ITEMS = ['729+ subtópicos BNCC', '17 disciplinas', 'PDF com gabarito em minutos']

/**
 * Hero da landing principal — foco professor (B2C). Sem framer-motion e sem
 * data fetching: tudo above-the-fold é estático ou animado via CSS
 * (motion-safe). h1 e screenshot são candidatos a LCP e não animam.
 */
const HeroSection: React.FC = () => {
  return (
    <header className="relative overflow-hidden bg-cream-50 dark:bg-ink-900">
      <div className="land-hero-glow" aria-hidden="true" />
      <div className="container relative mx-auto px-4 pt-28 pb-16 lg:pt-36 lg:pb-20">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <div>
              <p className="inline-flex items-center gap-2 bg-surface dark:bg-ink-700 border border-cream-400 dark:border-ink-500 rounded-full px-3 py-1 font-mono text-caderno-xs uppercase tracking-widest text-ink-500 dark:text-cream-300 mb-6 motion-safe:animate-hero-rise">
                <span className="w-1.5 h-1.5 rounded-full bg-moss-500" aria-hidden="true" />
                Banco curado · alinhado à BNCC
              </p>

              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-ink-900 dark:text-cream-50 leading-tight mb-6">
                Monte a prova em <span className="italic font-normal text-terra-500">5 minutos</span>. Não no
                domingo à noite.
              </h1>

              <p className="text-lg md:text-xl text-ink-500 dark:text-cream-300 max-w-xl mb-8">
                Filtre por disciplina, série e habilidade da BNCC, arraste as questões do banco curado e
                imprima um PDF profissional com gabarito. Sem Word, sem caçar questão em PDF antigo, sem fim
                de semana perdido.
              </p>

              <div className="flex flex-col sm:flex-row gap-3 mb-8 motion-safe:animate-hero-rise-1">
                <Link
                  to="/register"
                  className="px-7 py-3 bg-terra-500 hover:bg-terra-700 text-cream-50 rounded-caderno-xs text-base font-semibold shadow-caderno-lg hover:shadow-caderno-xl transition-all focus:outline-none focus:ring-4 focus:ring-terra-300 text-center"
                >
                  Começar grátis por 7 dias
                </Link>
                <a
                  href="#como-funciona"
                  className="px-7 py-3 bg-surface dark:bg-ink-700 hover:bg-cream-50 text-terra-500 dark:text-terra-300 border border-cream-400 dark:border-ink-500 rounded-caderno-xs text-base font-semibold shadow-caderno-md transition-all focus:outline-none focus:ring-4 focus:ring-terra-300 text-center"
                >
                  Ver como funciona
                </a>
              </div>

              <div className="motion-safe:animate-hero-rise-2">
                <ul className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-400 dark:text-ink-300 list-none p-0">
                  {RISK_BADGES.map((badge) => (
                    <li key={badge} className="flex items-center">
                      <CheckCircle className="w-4 h-4 text-moss-500 mr-2" aria-hidden="true" />
                      {badge}
                    </li>
                  ))}
                </ul>

                <p className="flex flex-wrap items-center gap-x-3 gap-y-1 border-t border-cream-300 dark:border-ink-700 pt-5 mt-6 text-sm text-ink-500 dark:text-cream-300">
                  {PROOF_ITEMS.map((item, i) => (
                    <React.Fragment key={item}>
                      {i > 0 && (
                        <span className="text-cream-400 dark:text-ink-500" aria-hidden="true">
                          ·
                        </span>
                      )}
                      <span className="font-medium">{item}</span>
                    </React.Fragment>
                  ))}
                </p>

                <p className="text-sm text-ink-400 dark:text-ink-300 mt-4">
                  É uma escola?{' '}
                  <Link
                    to="/lp/escola"
                    className="underline underline-offset-2 text-terra-500 dark:text-terra-300 hover:text-terra-700 dark:hover:text-terra-100 transition-colors"
                  >
                    Conheça o Plano Escolar →
                  </Link>
                </p>
              </div>
            </div>

            <HeroMockup />
          </div>

          <div className="hidden lg:flex justify-center mt-16">
            <a
              href="#como-funciona"
              className="inline-flex flex-col items-center gap-1 text-terra-500 hover:text-terra-700 dark:text-terra-300 dark:hover:text-terra-100 transition-colors motion-safe:animate-hero-bounce"
            >
              <span className="font-hand text-xl">veja como funciona</span>
              <ChevronDown className="w-5 h-5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </div>
    </header>
  )
}

export default HeroSection
